import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Grid,
  Typography,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { ExpandMoreIcon, DescriptionIcon } from 'components/common/Icon';
import { useCurrentAccount } from 'hooks/api/useAccounts';
import { useCurrentCompany } from 'hooks/api/useCompanies';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from 'theme';
import { IAccount } from 'services/account';
import { FormationsPrimaryButton } from 'components/common/Buttons';
import { BlobPreviewDialog } from 'components/FilePreview/BlobPreviewDialog';
import { TempDocument } from 'components/documentsV2/types';
import { HelloSignDialog } from 'components/documentSigning/HelloSignDialog';
import {
  useSaveHelloSignDocument,
  useSignedDocument,
} from 'hooks/api/useDropboxSign';
import { LoadFilePreviewDialog } from 'components/taxes/LoadFilePreviewDialog';
import { showToastOnError } from 'components/toast/showToast';
import { queryClient } from 'states/reactQueryClient';
import {
  DropboxSignTemplates,
  getDropboxSignTemplateURL,
} from 'services/dropboxSign';
import { DropboxSignTemplatesOptionToLabel } from 'enums';
import { StageSkeleton } from '../StageSkeleton';
import { AccordionCTA } from '../AccordionCTA';
import { StageStatusColumn, StageStatusText } from '../Components/StageStatusColumn';

const ReviewDocument = ({
  title,
  description,
  onClick,
  testId,
}: {
  title: string;
  description: string;
  onClick: (e: React.MouseEvent) => void;
  testId: string;
}) => {
  const handleClick = (e: React.MouseEvent) => {
    onClick(e);
  };
  return (
    <Grid
      container
      flexDirection="row"
      alignItems="flex-start"
      justifyContent="center"
      style={{
        cursor: 'pointer',
        boxSizing: 'border-box',
        fontWeight: 'bold',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: '#0D22593B',
        borderRadius: '4px',
        padding: 16,
        marginTop: 24,
        marginBottom: 24,
      }}
      onClick={handleClick}
      data-testid={testId}
    >
      <Grid item xs={1}>
        <DescriptionIcon />
      </Grid>
      <Grid item xs={11}>
        <Typography variant="subtitle1">{title}</Typography>
        <Typography variant="body2">{description}</Typography>
      </Grid>
    </Grid>
  );
};

interface ReviewAndSignComponentProps {
  account: IAccount;
  expanded: boolean | string;
  handleExpand: (
    panel: string,
  ) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => void;
  onContinueToNextSection: () => void;
  disabled: boolean;
}

export const ReviewAndSignBIB = ({
  account: accData,
  expanded,
  handleExpand,
  onContinueToNextSection,
  disabled,
}: ReviewAndSignComponentProps) => {
  const { t } = useTranslation();
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] =
    useState<boolean>(false);
  const [file, setFile] = useState<TempDocument>();
  const [showSignDialog, setShowSignDialog] = useState<boolean>(false);
  const [isPreviewSignedDialogOpen, setIsPreviewSignedDialogOpen] =
    useState<boolean>(false);

  const { currentCompany: businessData } = useCurrentCompany();
  const { signatureRequestTemplate } = businessData ?? {};

  const { signedDocuments } = useSignedDocument({
    businessId: businessData?.id,
    template: signatureRequestTemplate as DropboxSignTemplates,
  });

  const loadingSignedDocument: boolean = useMemo(() => {
    if (signedDocuments.length > 0) {
      return !signedDocuments[0].documentId;
    }
    return false;
  }, [signedDocuments]);

  const preparingSignatureRequestTemplate = useMemo(
    () => !signatureRequestTemplate,
    [signatureRequestTemplate],
  );

  const fileName = useMemo(() => {
    if (!signatureRequestTemplate) {
      return '';
    }
    return `${
      DropboxSignTemplatesOptionToLabel[
        signatureRequestTemplate as DropboxSignTemplates
      ]
    }.pdf`;
  }, [signatureRequestTemplate]);

  const showSignDialogHandler = () => {
    if (preparingSignatureRequestTemplate) {
      return;
    }
    setShowSignDialog(true);
  };

  const previewPackage = () => {
    if (preparingSignatureRequestTemplate) {
      return;
    }
    setIsPreviewDialogOpen(true);
  };

  const previewSignedPackage = () => {
    if (loadingSignedDocument) {
      return;
    }
    setIsPreviewSignedDialogOpen(true);
  };

  const continueToNext = () => {
    onContinueToNextSection();
  };

  const fetchFile = async (template: DropboxSignTemplates) => {
    const fileURL = getDropboxSignTemplateURL(template);
    await fetch(fileURL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/pdf',
      },
    }).then((response) => {
      response.blob().then((blob) => {
        const fBlob = new File([blob], fileName, {
          type: 'application/pdf',
        });
        setFile({
          file: fBlob,
          displayName: fileName,
          extension: 'pdf',
        });
      });
    });
  };

  useEffect(() => {
    if (signatureRequestTemplate) {
      fetchFile(signatureRequestTemplate as DropboxSignTemplates);
    }
    return () => {
      setFile(undefined);
    };
  }, [signatureRequestTemplate]);

  const completed: boolean = useMemo(
    () => signedDocuments?.length > 0,
    [signedDocuments],
  );
  const totalSteps = 1;

  const handleClose = () => {
    setIsPreviewDialogOpen(false);
  };

  const { saveHelloSignDocumentAsync } = useSaveHelloSignDocument({
    onSuccess: () =>
      queryClient.invalidateQueries([
        'dropboxSignedDocuments',
        businessData?.id ?? '',
      ]),
  });

  const saveSignature = async (signatureRequestID: string) => {
    setShowSignDialog(false);
    if (!signatureRequestTemplate) {
      return;
    }
    try {
      await saveHelloSignDocumentAsync({
        businessId: businessData?.id ?? '',
        signatureRequestId: signatureRequestID,
        templateName: signatureRequestTemplate as DropboxSignTemplates,
      });
    } catch (error) {
      showToastOnError(error);
    }
  };

  return (
    <Accordion
      expanded={expanded === 'review-and-sign'}
      onChange={handleExpand('review-and-sign')}
      disabled={disabled}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="review-and-sign-content"
        id="review-and-sign-header"
        sx={{
          alignItems: 'flex-start',
        }}
      >
        <Grid container>
          <Grid item xs={10}>
            <Typography variant="h6B" data-testid="review-and-sign-title">
              {t('progressTracker.reviewAndSign.title')}
            </Typography>
            <Typography
              variant="subtitle1"
              data-testid="review-and-sign-subtitle"
            >
              {t('progressTracker.reviewAndSign.subtitle')}
            </Typography>
            <Typography
              variant="body2"
              color={theme.palette.text.secondary}
              data-testid="review-and-sign-processingTime"
            >
              {t('progressTracker.reviewAndSign.processingTime')}
            </Typography>
            <AccordionCTA
              expanded={expanded}
              handleExpand={handleExpand}
              completed={completed ? 1 : 0}
              totalStep={1}
              testId="start-review-and-sign"
              accordionId="review-and-sign"
            />
          </Grid>
          <StageStatusColumn item xs={2}>
            <StageStatusText variant="h8B">
              {completed && <CheckCircle color="secondary" />}
              {completed ? totalSteps : 0}/{totalSteps}
            </StageStatusText>
          </StageStatusColumn>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <Grid
          container
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          spacing={2}
          sx={{ pl: 4, pr: 2 }}
        >
          {completed ? (
            <ReviewDocument
              title={fileName}
              description={
                loadingSignedDocument
                  ? 'We are preparing the signed document'
                  : 'View Signed Documents'
              }
              onClick={previewSignedPackage}
              testId="view-signed-documents"
            />
          ) : (
            <ReviewDocument
              title={fileName}
              description={
                preparingSignatureRequestTemplate
                  ? 'Formations is actively preparing the documents that require your signature. Please check back shortly to review and sign them.'
                  : 'This PDF contains important documents you must review and complete so that Formations can continue the incorporation process for your business.'
              }
              onClick={previewPackage}
              testId="review-smlc-signature-packet"
            />
          )}
          {completed ? (
            <FormationsPrimaryButton
              onClick={continueToNext}
              data-testid="continue-to-next"
            >
              {t('progressTracker.reviewAndSign.continue')}
            </FormationsPrimaryButton>
          ) : (
            <FormationsPrimaryButton
              onClick={showSignDialogHandler}
              data-testid="review-and-sign"
              disabled={preparingSignatureRequestTemplate}
            >
              {t('progressTracker.reviewAndSign.cta')}
            </FormationsPrimaryButton>
          )}
          <BlobPreviewDialog
            open={isPreviewDialogOpen}
            file={file}
            onClose={handleClose}
          />
          {isPreviewSignedDialogOpen && signedDocuments[0]?.documentId && (
            <LoadFilePreviewDialog
              documentId={signedDocuments[0].documentId}
              setSelectedDocumentId={() => setIsPreviewSignedDialogOpen(false)}
              accountId={accData.id}
            />
          )}
          {signatureRequestTemplate && showSignDialog && (
            <HelloSignDialog
              open
              onClose={() => setShowSignDialog(false)}
              handleSuccess={saveSignature}
              businessId={businessData?.id ?? ''}
              templateName={signatureRequestTemplate as DropboxSignTemplates}
            />
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

interface ReviewAndSignComponentBIBProps {
  expanded: boolean | string;
  handleExpand: (
    panel: string,
  ) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => void;
  onContinueToNextSection: () => void;
  disabled: boolean;
}

export const ReviewAndSignComponentBIB: React.FC<
  ReviewAndSignComponentBIBProps
> = ({ expanded, handleExpand, onContinueToNextSection, disabled }) => {
  const {
    currentCompany: businessData,
    isLoading: companyDataLoading,
    isError: isCompanyDataError,
  } = useCurrentCompany();
  const {
    currentAccount,
    isLoading: fetchingCurrentAccount,
    isError: isAccountError,
  } = useCurrentAccount();

  const { isLoading: loadingSignedDocument } = useSignedDocument({
    businessId: businessData?.id,
    template: businessData?.signatureRequestTemplate as DropboxSignTemplates,
  });

  if (fetchingCurrentAccount || companyDataLoading || loadingSignedDocument) {
    return <StageSkeleton />;
  }
  if (businessData != null && currentAccount != null) {
    return (
      <ReviewAndSignBIB
        account={currentAccount}
        handleExpand={handleExpand}
        expanded={expanded}
        onContinueToNextSection={onContinueToNextSection}
        disabled={disabled}
      />
    );
  }
  if (isCompanyDataError && isAccountError) {
    return (
      <Alert severity="error">
        Error fetching data. Please try to refresh or contact for support
      </Alert>
    );
  }
  return null;
};
